import React from "react";
import { Box, Button, Paper, Stack, Typography } from "@mui/material";
import type {
  SpecType,
  SpecsValidationIssue,
} from "../../../service/SpecsRevisionService";
import { createStyles, palette, spacing } from "../../../styles/theme";
import type { ObservableModelRoot } from "../specsModel/observable";
import { getSpecsIssueMatchForModelPath } from "../specsValidationIssues";
import {
  getValueAtModelPath,
  joinModelPath,
  setValueAtModelPath,
} from "./modelPath";
import SpecsFormEntriesGrid from "./SpecsFormEntriesGrid";
import {
  getValidationIssueFrameSx,
  SpecsValidationIssueHint,
} from "./SpecsValidationIssueHighlight";
import type { SpecsFormEntry, SpecsFormObjectSchema } from "./types";

const styles = createStyles({
  nestedPaper: {
    p: spacing.groupingDesktop,
    bgcolor: palette.grey.lightBackground,
    border: `1px solid ${palette.grey.lightGrey}`,
  },
});

type SpecsObjectSectionProps = {
  model: ObservableModelRoot;
  entry: SpecsFormObjectSchema;
  basePath: string;
  validationIssues?: SpecsValidationIssue[];
  validationSpecType?: SpecType;
  renderEntry: (entry: SpecsFormEntry, basePath: string) => React.ReactNode;
};

export default function SpecsObjectSection({
  model,
  entry,
  basePath,
  validationIssues = [],
  validationSpecType,
  renderEntry,
}: SpecsObjectSectionProps) {
  const objectPath = joinModelPath(basePath, entry.path);
  const value = getValueAtModelPath(model, objectPath);
  const issueMatch = validationSpecType
    ? getSpecsIssueMatchForModelPath({
        issues: validationIssues,
        specType: validationSpecType,
        modelPath: objectPath,
        includeAncestorTargets: true,
      })
    : null;
  const paperSx = {
    ...styles.nestedPaper,
    ...getValidationIssueFrameSx(issueMatch),
  };

  if (!value) {
    return (
      <Paper variant="outlined" sx={paperSx}>
        <Stack spacing={1.5}>
          <Typography variant="subtitle2">{entry.title}</Typography>
          <SpecsValidationIssueHint match={issueMatch} />
          {entry.description ? (
            <Typography variant="body2" color="text.secondary">
              {entry.description}
            </Typography>
          ) : null}
          <Box>
            <Button
              variant="outlined"
              size="small"
              onClick={() =>
                setValueAtModelPath(model, objectPath, entry.createValue())
              }
            >
              Add {entry.title}
            </Button>
          </Box>
        </Stack>
      </Paper>
    );
  }

  return (
    <Paper variant="outlined" sx={paperSx}>
      <Stack spacing={2}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="subtitle2">{entry.title}</Typography>
          <Box sx={{ flex: 1 }} />
          {entry.removable !== false ? (
            <Button
              variant="outlined"
              size="small"
              color="inherit"
              onClick={() => setValueAtModelPath(model, objectPath, null)}
            >
              Remove
            </Button>
          ) : null}
        </Stack>
        <SpecsValidationIssueHint match={issueMatch} />
        {entry.description ? (
          <Typography variant="body2" color="text.secondary">
            {entry.description}
          </Typography>
        ) : null}
        <SpecsFormEntriesGrid
          entries={entry.entries}
          basePath={objectPath}
          columns={entry.columns}
          renderEntry={(childEntry) => renderEntry(childEntry, objectPath)}
        />
      </Stack>
    </Paper>
  );
}
